import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";

import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";

import { Notification, useTranslate, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigDelete } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginTop: "6em",
  },
  form: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 600,
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 600,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    width: "100%",
    maxWidth: 1200,
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.grey[500],
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  email?: string;
}

interface Passkey {
  id: number;
  credential_id: string;
  device_name: string;
  device_type: string;
  os_name: string;
  os_version: string;
  browser_name: string;
  is_active: boolean;
  failed_attempts: number;
  last_used: string;
  registered_ip: string;
  last_used_ip: string;
  created_at: string;
  updated_at: string;
}

const { Form } = withTypes<FormValues>();

const ManageUserPasskeys = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]); // Array of arrays: [headers, ...rows]
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passkeyToDelete, setPasskeyToDelete] = useState<Passkey | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const handleSearch = (inputData: any) => {
    const { email } = inputData;
    setLoading(true);
    setCurrentEmail(email);

    const config = getConfigGet({ email: email });

    axios
      .get(ENDPOINTS.MANAGE_USER_PASSKEYS, config)
      .then((res: any) => {
        // Backend wraps response as { status, data, meta }
        // axios wraps it again, so we need res.data.data
        const responseData = res.data?.data || res.data;
        
        // Backend returns [headers, ...rows] format
        if (Array.isArray(responseData) && responseData.length > 0) {
          setTableData(responseData);
          const rowCount = responseData.length - 1; // Subtract 1 for headers row
          if (rowCount === 0) {
            notify("No passkeys found for this user", "info", "", undefined, 5000);
          } else {
            notify(`Found ${rowCount} passkey(s)`, "info", "", undefined, 5000);
          }
        } else {
          setTableData([]);
          notify("No passkeys found for this user", "info", "", undefined, 5000);
        }
        
        setLoading(false);
      })
      .catch((error) => {
        notify(
          error?.response?.data?.message || `Failed to fetch passkeys`,
          "error",
          "",
          undefined,
          50000
        );
        setTableData([]);
        setLoading(false);
      });
  };

  const handleDeleteClick = (passkey: Passkey) => {
    setPasskeyToDelete(passkey);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!passkeyToDelete || !currentEmail) return;

    setDeleting(true);
    let headers = getConfigDelete();
    let params = {
      email: currentEmail,
    };

    axios
      .delete(`${ENDPOINTS.MANAGE_USER_PASSKEYS}/${passkeyToDelete.id}`, { headers, params })
      .then((res: any) => {
        notify("Passkey removed successfully", "info", "", undefined, 5000);
        setDeleteDialogOpen(false);
        setPasskeyToDelete(null);
        setDeleting(false);
        // Refresh the list
        handleSearch({ email: currentEmail });
      })
      .catch((error) => {
        notify(
          error?.response?.data?.message || `Failed to remove passkey`,
          "error",
          "",
          undefined,
          50000
        );
        setDeleting(false);
      });
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPasskeyToDelete(null);
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email) {
      errors.email = translate("ra.validation.required");
    }
    return errors;
  };

  return (
    <div className={classes.main}>
      <Form
        onSubmit={handleSearch}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate>
            <div className={classes.form}>
              <div className={classes.input}>
                <Field
                  autoFocus
                  name="email"
                  // @ts-ignore
                  component={renderInput}
                  label={translate("pos.email")}
                  disabled={loading}
                />
              </div>
            </div>
            <CardActions className={classes.actions}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                fullWidth
              >
                {loading && <CircularProgress size={25} thickness={2} />}
                Search Passkeys
              </Button>
            </CardActions>
          </form>
        )}
      />

      {tableData.length > 0 && (
        <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} aria-label="passkeys table">
              <TableHead>
                <TableRow>
                  {tableData[0].map((header: string, index: number) => (
                    <TableCell key={index}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.slice(1).map((row: any[], rowIndex: number) => {
                  // Extract passkey object from last column (index 11)
                  const passkey = row[11] as Passkey;
                  
                  return (
                    <TableRow key={rowIndex}>
                      {/* ID - index 0 */}
                      <TableCell>{row[0]}</TableCell>
                      {/* User ID - index 1 */}
                      <TableCell>{row[1]}</TableCell>
                      {/* Device Name - index 2 */}
                      <TableCell>{row[2]}</TableCell>
                      {/* Device Type - index 3 */}
                      <TableCell>
                        {row[3] !== 'N/A' ? (
                          <Chip
                            label={row[3]}
                            size="small"
                            className={classes.statusChip}
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      {/* OS Name - index 4 */}
                      <TableCell>{row[4]}</TableCell>
                      {/* OS Version - index 5 */}
                      <TableCell>{row[5]}</TableCell>
                      {/* Browser Name - index 6 */}
                      <TableCell>{row[6]}</TableCell>
                      {/* Status - index 7 (already formatted by backend) */}
                      <TableCell>
                        <Chip
                          label={row[7]}
                          size="small"
                          color={row[7] === 'Active' ? 'primary' : 'default'}
                          className={classes.statusChip}
                        />
                      </TableCell>
                      {/* Failed Attempts - index 8 */}
                      <TableCell>{row[8]}</TableCell>
                      {/* Last Failed At - index 9 (already formatted by backend) */}
                      <TableCell>{row[9]}</TableCell>
                      {/* Last Used - index 10 (already formatted by backend) */}
                      <TableCell>{row[10]}</TableCell>
                      {/* Actions - index 11 (passkey object) */}
                      <TableCell>
                        <IconButton
                          aria-label="delete"
                          color="secondary"
                          onClick={() => handleDeleteClick(passkey)}
                          disabled={deleting || !passkey.is_active}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove this passkey?
            <br />
            <strong>Device:</strong> {passkeyToDelete?.device_name || "N/A"}
            <br />
            <strong>Type:</strong> {passkeyToDelete?.device_type || "N/A"}
            <br />
            <strong>OS:</strong>{" "}
            {passkeyToDelete?.os_name && passkeyToDelete?.os_version
              ? `${passkeyToDelete.os_name} ${passkeyToDelete.os_version}`
              : passkeyToDelete?.os_name || "N/A"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default ManageUserPasskeys;

